import dayjs from "dayjs";
import {
	DashboardReportType,
	LessonCounts,
	LessonMonths,
	ReportPeriod,
	StatusCounts,
	TaskBucket,
	Urgency,
} from "../../types/dashboard.types";

/** A chart series. Its colour is the same in every theme. */
export interface Series<K extends string = string> {
	key: K;
	label: string;
	/** A shorter name for tight spots, e.g. a legend above a narrow chart. */
	short?: string;
	color: string;
}

/** A Recharts legend `itemSorter` keeping the series order (its default is alphabetical). */
export const seriesOrder =
	(series: Series[]) =>
	(item: { dataKey?: unknown }): number =>
		series.findIndex((serie) => serie.key === item.dataKey);

export const BADGES = {
	reports: "RE",
	lessons: "LL",
	projects: "PD",
	tasks: "MT",
} as const;

export const ALL_TENANTS_LABEL = "All tenants";

/** Report types in display order, with the full names the Reporting page uses. */
export const REPORT_TYPES: Array<
	Series<DashboardReportType> & { short: string }
> = [
	{
		key: "incident_report",
		short: "Incident",
		label: "Incident Report (IR)",
		color: "#8979ff",
	},
	{ key: "audit", short: "Audit", label: "Audit", color: "#ff928a" },
	{
		key: "moc",
		short: "MoC",
		label: "Management of Change (MoC)",
		color: "#3cc3df",
	},
	{
		key: "ofi",
		short: "OFI",
		label: "Opportunity for Improvement (OFI)",
		color: "#ffae4c",
	},
	{
		key: "non_conformance",
		short: "NCR",
		label: "Non-conformance report (NCR)",
		color: "#d4e157",
	},
];

/** Report types as filter options; only `enabled` ones (the tenant's) once known. */
export const reportTypeOptions = (enabled?: DashboardReportType[]) =>
	REPORT_TYPES.filter((type) => !enabled || enabled.includes(type.key)).map(
		({ key, label }) => ({ value: key, label })
	);

export const REPORT_STATUS_SERIES: Series<keyof StatusCounts>[] = [
	{ key: "draft", label: "Draft", color: "#6c757d" },
	{ key: "open", label: "Open", color: "#0ca4ab" },
	{ key: "closed", label: "Closed", color: "#0dd949" },
];

export const LESSON_STATUS_SERIES: Series<keyof StatusCounts>[] = [
	{ key: "draft", label: "Draft", color: "#6c757d" },
	{ key: "open", label: "Open", color: "#00bebe" },
	{ key: "closed", label: "Closed", color: "#00ff44" },
];

export const LESSON_SERIES: Series<keyof LessonCounts>[] = [
	{ key: "internal", label: "Internal", color: "#8072ec" },
	{ key: "external", label: "External", color: "#e88885" },
];

export const URGENCY_SERIES: Series<Urgency>[] = [
	{ key: "overdue", label: "Overdue", color: "#ff6b6b" },
	{ key: "attention", label: "Needs attention", color: "#ffae4c" },
	{ key: "onTrack", label: "On track", color: "#0ca4ab" },
];

/** Task types in display order; optional ones only show when non-zero. */
export const TASK_BUCKETS: Array<{
	key: TaskBucket;
	label: string;
	optional?: boolean;
}> = [
	{ key: "audit", label: "Audit" },
	{ key: "incident", label: "Incident" },
	{ key: "moc", label: "MoC" },
	{ key: "ofi", label: "OFI" },
	{ key: "ncr", label: "NCR" },
	{ key: "lesson", label: "Lesson" },
	{ key: "project", label: "Project" },
	{ key: "timesheet", label: "Timesheet", optional: true },
	{ key: "other", label: "Other", optional: true },
];

export const REPORT_PERIOD_OPTIONS: Array<{
	value: ReportPeriod;
	label: string;
}> = [
	{ value: "week", label: "Past week" },
	{ value: "month", label: "Past month" },
	{ value: "quarter", label: "Past quarter" },
	{ value: "year", label: "Past year" },
];

export const LESSON_MONTH_OPTIONS: Array<{
	value: LessonMonths;
	label: string;
}> = [
	{ value: 12, label: "Past 12 months" },
	{ value: 24, label: "Past 24 months" },
];

/** The current year and the two before it, in UTC as the server buckets them. */
export const selectableYears = (now = new Date()): number[] => {
	const year = now.getUTCFullYear();
	return [year, year - 1, year - 2];
};

export const yearOptions = (now = new Date()) =>
	selectableYears(now).map((year) => ({ value: year, label: String(year) }));

/** A "YYYY-MM" month key formatted with a dayjs pattern, e.g. "MMM YYYY". */
export const formatMonthKey = (monthKey: string, pattern: string): string =>
	dayjs(`${monthKey}-01`).format(pattern);

/** A count; `partial` marks a lower bound (a source limit was reached) as "N+". */
export const formatCount = (value: number, partial = false): string =>
	`${value.toLocaleString()}${partial ? "+" : ""}`;

/** "Elemental Energies 3 · other tenants 5" for the all-tenants view. */
export const formatOwnSplit = (
	ownLabel: string,
	own: number,
	all: number
): string =>
	`${ownLabel} ${formatCount(own)} · other tenants ${formatCount(all - own)}`;
